import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { SESSION_CONFIG } from "../../features/auth/constants";
import { storage } from "../../lib/storage";
import {
  clearRequestContext,
  setRequestContext,
} from "../../lib/requestContext";

import type { Role, UserBorefieldRole } from "../../features/auth/types";

/** @deprecated Use Role from auth/types instead */
export type UserRole = Role;

export interface User {
  id: string;
  email: string;
  name: string;
  /** @deprecated Use borefieldRoles for per-borefield role assignment */
  role: Role;
  borefieldRoles?: UserBorefieldRole[];
  memberships: {
    organizationId: string;
    organizationName: string;
    role: Role;
  }[];
  avatarUrl?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Has the localStorage session-restore attempt completed (either way)?
   *  Route guards MUST wait on this. See initialState for why. */
  isInitialized: boolean;
  /** Which organization the user is acting as. A SELECTION among their own memberships — the
   *  server re-verifies it and is what actually decides. `null` before selection. */
  activeOrganizationId: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresAt: string;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  // FALSE until initializeAuth settles. Without this flag a refresh logged the
  // user out: initializeAuth is async and effects run AFTER render, so on the
  // first paint isAuthenticated was false and isLoading was false, and
  // ProtectedRoute could not tell "not logged in" from "haven't looked yet" --
  // so it redirected to /login before the stored session was ever read.
  isInitialized: false,
  activeOrganizationId: null,
};

// Demo mode - bypasses API authentication for local development
const DEMO_MODE = true;

const DEMO_USER: User = {
  id: "demo-user-001",
  email: "demo@geothermal.app",
  name: "Demo User",
  role: "super_admin",
  // TWO memberships on the demo user, deliberately. A single-membership fixture lets the
  // org-switching path rot untested, and switching orgs is the model — not an edge case.
  memberships: [
    {
      organizationId: "org-demo-operator",
      organizationName: "Demo Operator",
      role: "super_admin",
    },
    {
      organizationId: "org-demo-supplier",
      organizationName: "Demo Mud Services",
      role: "consultant",
    },
  ],
  createdAt: new Date().toISOString(),
  lastLoginAt: new Date().toISOString(),
};

// Async thunks
export const login = createAsyncThunk<LoginResponse, LoginCredentials>(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    // Demo mode: bypass API and return mock user
    if (DEMO_MODE) {
      const demoResponse: LoginResponse = {
        user: {
          ...DEMO_USER,
          email: credentials.email,
          name: credentials.email.split("@")[0] ?? credentials.email,
        },
        token: "demo-token-" + Date.now(),
        refreshToken: "demo-refresh-" + Date.now(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      };

      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, demoResponse.token);
      storage.setItem(
        SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY,
        demoResponse.refreshToken,
      );
      storage.setItem(
        SESSION_CONFIG.USER_STORAGE_KEY,
        JSON.stringify(demoResponse.user),
      );

      return demoResponse;
    }

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData: { message?: string } = (await response
          .json()
          .catch(() => ({}))) as { message?: string };
        return rejectWithValue(errorData.message ?? "Login failed");
      }

      const data = (await response.json()) as LoginResponse;

      // Store tokens in localStorage
      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, data.token);
      storage.setItem(
        SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY,
        data.refreshToken,
      );
      storage.setItem(
        SESSION_CONFIG.USER_STORAGE_KEY,
        JSON.stringify(data.user),
      );

      return data;
    } catch {
      return rejectWithValue("Network error. Please try again.");
    }
  },
);

export const refreshToken = createAsyncThunk<{ token: string }, void>(
  "auth/refreshToken",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const currentRefreshToken = state.auth.refreshToken;

      if (!currentRefreshToken) {
        return rejectWithValue("No refresh token available");
      }

      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) {
        return rejectWithValue("Token refresh failed");
      }

      const data = (await response.json()) as { token: string };

      // Update stored token
      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, data.token);

      return data;
    } catch {
      return rejectWithValue("Network error during token refresh");
    }
  },
);

export const initializeAuth = createAsyncThunk<LoginResponse | null, void>(
  "auth/initialize",
  () => {
    // Try to restore session from localStorage
    const token = storage.getItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
    const refreshTokenStored = storage.getItem(
      SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY,
    );
    const userJson = storage.getItem(SESSION_CONFIG.USER_STORAGE_KEY);

    if (token && refreshTokenStored && userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        return {
          user,
          token,
          refreshToken: refreshTokenStored,
          expiresAt: "", // Will be validated on next API call
        };
      } catch {
        // Clear invalid data
        storage.removeItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
        storage.removeItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
        storage.removeItem(SESSION_CONFIG.USER_STORAGE_KEY);
      }
    }
    return null;
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.activeOrganizationId = null;
      // The spine's reader cannot see this store, so it is TOLD. Missing this on logout leaves a
      // dead token on every subsequent spine fetch.
      clearRequestContext();

      // Clear stored tokens
      storage.removeItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
      storage.removeItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
      storage.removeItem(SESSION_CONFIG.USER_STORAGE_KEY);
    },
    clearError(state) {
      state.error = null;
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      // A single-membership user has nothing to choose; default them in so the header is always
      // present rather than present-only-after-a-click.
      // `memberships` may be ABSENT, and not only in fixtures: a session restored from
      // localStorage was serialized when the shape was `tenantId: string`, so a returning user
      // hits this path with the old object. Tolerate it rather than throwing them to a blank page.
      const memberships = action.payload.memberships ?? [];
      if (!state.activeOrganizationId && memberships.length === 1) {
        state.activeOrganizationId = memberships[0]!.organizationId;
        setRequestContext({
          activeOrganizationId: state.activeOrganizationId,
        });
      }
    },
    tokenRefreshed(state, action: PayloadAction<string>) {
      state.token = action.payload;
      // Update stored token
      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, action.payload);
      setRequestContext({ token: action.payload });
    },

    /**
     * Switch which organization the user is acting as.
     *
     * REFUSES an organization the user is not a member of. That refusal is a UI correctness
     * guard, NOT a security control — the server re-verifies on every request and is the only
     * thing that actually decides. A client that can name an org can name someone else's.
     */
    setActiveOrganization(state, action: PayloadAction<string>) {
      const isMember = (state.user?.memberships ?? []).some(
        (m) => m.organizationId === action.payload,
      );
      if (!isMember) return;
      state.activeOrganizationId = action.payload;
      setRequestContext({ activeOrganizationId: action.payload });
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      });

    // Refresh token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
      })
      .addCase(refreshToken.rejected, (state) => {
        // If refresh fails, log the user out
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });

    // Initialize auth from storage
    builder
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
        }
        // Settled either way -- guards may now decide.
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        // A failed restore is still a completed check; never strand the guard.
        state.isInitialized = true;
      });
  },
});

export const {
  logout,
  clearError,
  setUser,
  tokenRefreshed,
  setActiveOrganization,
} = authSlice.actions;

export const authReducer = authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectAuthInitialized = (state: { auth: AuthState }) =>
  state.auth.isInitialized;
